import { Link } from '@tanstack/react-router';
import { GalleryHorizontalEnd, Image } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { blockKinds, type BlockKind } from '../../schemas/block-form-schema';

export interface BlockKindChooserProps {
  /** Null on the shared blocks page. */
  storeId: string | null;
}

const ICONS = { banner: Image, rail: GalleryHorizontalEnd } as const;

const CARD_CLASS =
  'bg-card text-card-foreground hover:border-primary focus-visible:ring-ring/50 flex h-full flex-col gap-3 rounded-xl border p-5 shadow-sm transition-colors focus-visible:ring-[3px] focus-visible:outline-none';

/** First step of creating a block: two cards, one per kind, each linking to that kind's editor. */
export function BlockKindChooser({ storeId }: BlockKindChooserProps) {
  const { t } = useTranslation('content');

  const card = (kind: BlockKind) => {
    const Icon = ICONS[kind];
    return (
      <>
        <Icon className="text-primary size-6" aria-hidden />
        <span className="text-base font-semibold">{t(`kind.${kind}`)}</span>
        <span className="text-muted-foreground text-sm">{t(`kindHint.${kind}`)}</span>
        {storeId === null && kind === 'rail' ? (
          <span className="text-muted-foreground text-xs">{t('rail.sharedNote')}</span>
        ) : null}
      </>
    );
  };

  return (
    <ul
      className="grid gap-4 sm:grid-cols-2"
      aria-label={t('block.kind')}
      data-tour="content.block-new.kinds"
    >
      {blockKinds.map((kind) => (
        <li
          key={kind}
          data-tour={
            kind === 'banner' ? 'content.block-new.kind-banner' : 'content.block-new.kind-rail'
          }
        >
          {storeId === null ? (
            <Link to="/org/shared-blocks/new/$kind" params={{ kind }} className={CARD_CLASS}>
              {card(kind)}
            </Link>
          ) : (
            <Link
              to="/s/$storeId/content/blocks/new/$kind"
              params={{ storeId, kind }}
              className={CARD_CLASS}
            >
              {card(kind)}
            </Link>
          )}
        </li>
      ))}
    </ul>
  );
}
